import { c as _c } from "react/compiler-runtime";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "cn";
import Link from "next/link";
import { externalLinkProps } from "@/lib/utils";
const buttonVariants = cva("inline-flex shrink-0 touch-manipulation select-none items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium text-sm transition-[color,background-color,border-color,fill,box-shadow,scale] duration-150 ease-out will-change-transform active:scale-[0.97] disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-red-500 aria-invalid:focus:ring-red-500/20 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0", {
  defaultVariants: {
    size: "lg",
    variant: "primary"
  },
  variants: {
    size: {
      icon: "size-9",
      "icon-lg": "size-10",
      "icon-sm": "size-8 rounded-md",
      lg: "h-10 gap-1.5 rounded-[10px] px-4 text-sm",
      md: "h-9 gap-1.5 rounded-lg px-3 text-sm",
      sm: "h-8 gap-1.5 rounded-lg px-2.5 text-sm",
      xl: "h-11 gap-1.5 rounded-lg px-6 text-base"
    },
    variant: {
      destructive: "bg-red-500 text-white hover:bg-red-600 disabled:bg-gray-300 disabled:text-fg-muted disabled:[&_svg]:fill-gray-900",
      ghost: "hover:bg-gray-300 hover:text-fg",
      link: "text-fg underline-offset-4 hover:underline",
      outline: "bg-surface-overlay shadow-raised hover:text-fg",
      primary: "bg-gray-1200 text-gray-100",
      secondary: "bg-surface text-fg shadow-raised hover:bg-gray-200 hover:shadow-raised-hover"
    }
  }
});
interface StyleProps extends VariantProps<typeof buttonVariants> {
  className?: string;
}
type ButtonProps = StyleProps & (({
  href: string;
} & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href">) | ({
  href?: undefined;
} & React.ButtonHTMLAttributes<HTMLButtonElement>));
function Button(t0) {
  const $ = _c(22);
  let className;
  let props;
  let size;
  let variant;
  if ($[0] !== t0) {
    ({
      className,
      variant,
      size,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = size;
    $[4] = variant;
  } else {
    className = $[1];
    props = $[2];
    size = $[3];
    variant = $[4];
  }
  let t1;
  if ($[5] !== className || $[6] !== size || $[7] !== variant) {
    t1 = cn(buttonVariants({
      className,
      size,
      variant
    }));
    $[5] = className;
    $[6] = size;
    $[7] = variant;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const classes = t1;
  if (props.href !== undefined) {
    let anchorProps;
    let href;
    if ($[9] !== props) {
      ({
        href,
        ...anchorProps
      } = props);
      $[9] = props;
      $[10] = anchorProps;
      $[11] = href;
    } else {
      anchorProps = $[10];
      href = $[11];
    }
    let t2;
    if ($[12] !== href) {
      t2 = externalLinkProps(href);
      $[12] = href;
      $[13] = t2;
    } else {
      t2 = $[13];
    }
    let t3;
    if ($[14] !== anchorProps || $[15] !== classes || $[16] !== href || $[17] !== t2) {
      t3 = <Link className={classes} data-slot="button" href={href} {...t2} {...anchorProps} />;
      $[14] = anchorProps;
      $[15] = classes;
      $[16] = href;
      $[17] = t2;
      $[18] = t3;
    } else {
      t3 = $[18];
    }
    return t3;
  }
  let t2;
  if ($[19] !== classes || $[20] !== props) {
    t2 = <button className={classes} data-slot="button" {...props} />;
    $[19] = classes;
    $[20] = props;
    $[21] = t2;
  } else {
    t2 = $[21];
  }
  return t2;
}
export { Button };